import React, { FC } from "react";
import { useNavigate } from "react-router-dom";

import { ColorPrimary, IgPaper, IgTypography } from "@intelligems/ig-theme";
import { Grid } from "@mui/material";

import { useGetOrg } from "../../../../api/organizations/get-organization";
import {
  checkAnyScope,
  checkBooleanScope,
} from "../../../../components/ScopeWrappers/helpers";
import { ScopedButton } from "../../../../components/ScopeWrappers/ScopedButton";
import { AudienceTargetingIcon } from "../../../../images/Icons/AudienceTargetingIcon";
import { SplitUrlTestIconFilled } from "../../../../images/Icons/ContentTest/SplitUrlTest/SplitUrlTestIconFilled";
import { OfferIcon } from "../../../../images/Icons/Offer/OfferIcon";
import { PlayPauseIcon } from "../../../../images/Icons/Offer/PlayPauseIcon";
import { RolloutIcon } from "../../../../images/Icons/Offer/RolloutIcon";
import { TargetVisitorsIcon } from "../../../../images/Icons/Offer/TargetVisitorsIcon";
import {
  CAMPAIGN_ROUTES,
  PERSONALIZATION_ROUTES,
} from "../../../../routes/routes";
import theme from "../../../../theme";
import { personalizationsGettingStarted } from "../../../../utils/linkSheet";
import { mpClickCreateCampaignButton } from "../../../../utils/mixpanel";
import openNewTab from "../../../../utils/openNewTab";

const informationCards = [
  {
    title: "Modify Your Shop",
    icon: (
      <OfferIcon
        sx={{
          [theme.breakpoints.down("lg")]: {
            width: "63px",
            height: "52px",
          },
          [theme.breakpoints.up("lg")]: {
            width: "83px",
            height: "72px",
          },
        }}
      />
    ),
    description:
      "Offer discounts and deals, overhaul your branding, fine tune your text and layouts, or combine many changes to create a compelling experience for visitors.",
  },
  {
    title: "Target Specific Visitors",
    icon: (
      <TargetVisitorsIcon
        sx={{
          [theme.breakpoints.down("lg")]: {
            width: "63px",
            height: "52px",
          },
          [theme.breakpoints.up("lg")]: {
            width: "83px",
            height: "72px",
          },
        }}
      />
    ),
    description:
      "Apply changes to all site visitors, or limit them to specific audiences like new visitors, those from a social campaign, a specific country, or a particular device.",
  },
  {
    title: "Start & Stop Anytime",
    icon: (
      <PlayPauseIcon
        sx={{
          [theme.breakpoints.down("lg")]: {
            width: "63px",
            height: "52px",
          },
          [theme.breakpoints.up("lg")]: {
            width: "83px",
            height: "72px",
          },
        }}
      />
    ),
    description:
      "Leave a personalization active year round to optimize your site for specific audience, run it briefly for an event, or turn it on every year for seasonal promotions.",
  },
  {
    title: "Roll Out Test Groups",
    icon: (
      <RolloutIcon
        sx={{
          [theme.breakpoints.down("lg")]: {
            width: "63px",
            height: "52px",
          },
          [theme.breakpoints.up("lg")]: {
            width: "83px",
            height: "72px",
          },
        }}
      />
    ),
    description:
      "Roll out an experiment’s winning variant into a live Personalization in a few clicks, optimizing your site immediately without any Shopify or development work.",
  },
];

export const NoPersonalizationsView = () => {
  const organization = useGetOrg();
  const navigate = useNavigate();
  return (
    <Grid item container justifyContent={"center"}>
      <Grid item mt={7} sx={{ marginBottom: "-40px" }}>
        <IgTypography variant={"h3"} igStyle={"semibold"}>
          Personalizations
        </IgTypography>
      </Grid>
      <Grid
        marginTop={"70px"}
        item
        container
        alignItems={"center"}
        justifyContent={"center"}
        textAlign={"center"}
      >
        <IgTypography
          sx={{
            maxWidth: "703px",
            fontSize: "18px",
            lineHeight: "28px",
            letterSpacing: "0.5px",
          }}
        >
          Optimize every shopping experience by offering deals and fine tuning
          how every visitor sees your site.{" "}
          <IgTypography
            onClick={() => {
              openNewTab(
                personalizationsGettingStarted,
                "Personalizations - Getting Started"
              );
            }}
            igStyle={"link"}
            sx={{
              maxWidth: "703px",
              fontSize: "18px",
              lineHeight: "28px",
              letterSpacing: "0.5px",
              fontWeight: 400,
            }}
          >
            Learn more.
          </IgTypography>
        </IgTypography>
      </Grid>
      {organization.isSuccess && (
        <Grid item textAlign={"center"} marginTop={"40px"}>
          <ScopedButton
            customBackgroundColor={ColorPrimary.main}
            variant="contained"
            size="large"
            debugId={"create-first-personalization-button"}
            onClick={() => {
              mpClickCreateCampaignButton();
              navigate(`${PERSONALIZATION_ROUTES.Personalization}?action=new`);
            }}
            isEnabled={checkAnyScope([
              checkBooleanScope(
                "personalizations",
                organization.data.org.configurations
              ),
            ])}
          >
            New Personalization
          </ScopedButton>
        </Grid>
      )}
      <Grid
        item
        container
        direction={"row"}
        justifyContent={"center"}
        marginTop={"40px"}
        columnSpacing={1}
        rowSpacing={1}
      >
        {informationCards.map((card) => (
          <Grid xs={3} item sx={{ maxWidth: "230px" }}>
            <IgPaper
              sx={{
                paddingTop: "40px",
                maxWidth: "230px",
                height: "345px",
                [theme.breakpoints.down("xl")]: {
                  padding: "15px 15px",
                  height: "360px",
                },
                [theme.breakpoints.down("lg")]: {
                  padding: "15px 15px",
                  height: "440px",
                },
                [theme.breakpoints.down("md")]: {
                  padding: "15px 15px",
                  height: "440px",
                },
                [theme.breakpoints.down("sm")]: {
                  padding: "15px 15px",
                  height: "440px",
                },
              }}
            >
              <Grid
                item
                container
                direction={"column"}
                justifyContent={"center"}
                textAlign={"center"}
                rowSpacing={2}
              >
                <Grid item>{card.icon}</Grid>
                <Grid item>
                  <IgTypography
                    sx={{
                      [theme.breakpoints.down("md")]: {
                        fontSize: "12px",
                      },
                    }}
                    igStyle={"semibold"}
                  >
                    {card.title}
                  </IgTypography>
                </Grid>
                <Grid item>
                  <IgTypography
                    sx={{
                      maxWidth: "167px",
                      color: "#6C6C6C",
                      [theme.breakpoints.down("md")]: {
                        fontSize: "12px",
                        maxWidth: "none",
                      },
                    }}
                  >
                    {card.description}
                  </IgTypography>
                </Grid>
              </Grid>
            </IgPaper>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
